import React, { useState, useEffect } from 'react';
import WMS_QuoteGrid from './WMS_QuoteGrid';
import { useAuth } from '../../context/wms-context.js';

import logError from '../../../utils/errorUtil.js';
import WMS_QuoteSubmission from './WMS_QuoteSubmission.js';
import { PAGE_TYPES } from './data.js';
import { WMSFetch } from '../../utils/common-api-calls.js';

export default function SmsHomePage() {
    const { currentUser } = useAuth();

    const [loading, setLoading] = useState(false);

    const [activePage, setActivePage] = useState(PAGE_TYPES.QUOTE_GRID);
    const [users, setUsers] = useState([]);
    const [sources, setSources] = useState([]);
    const [autoQuoteData, setAutoQuoteData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [partsWithInventory, setPartsWithInventory] = useState([]);
    const [showInStock, setShowInStock] = useState(false);
    const [conditionCodes, setConditionCodes] = useState([]);

    const [expandAll, setExpandAll] = useState(() => {
        const saved = localStorage.getItem('wms_sms_expand_all');
        return saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem('wms_sms_expand_all', expandAll ? 'true' : 'false');
    }, [expandAll]);

    const [filters, setFilters] = useState({
        partName: '',
        quotes: '',
        selected: '',
        quantityRequested: '',
        customer: '',
        customerType: [],
        quoteId: '',
        CreatedDate: '',
        inscor__Quote_Source__c: [],
        inscor__Status__c: '',
        conditionCodeToDisplay: [],
        inscor__Temp_Priority_Code_Name__c: '',
        quoteExclusionReason: '',
        partExclusionReason: '',
        processed: '',
        accountOwnerName: [],
        salesAgentName: [],
    });

    // Try to restore filter date
    const [filterDate, setFilterDate] = useState(() => {
        const today = new Date();
        const opts = { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Los_Angeles' };
        const parts = new Intl.DateTimeFormat('en-CA', opts).formatToParts(today);
        const yyyy = parts.find((p) => p.type === 'year').value;
        const mm = parts.find((p) => p.type === 'month').value;
        const dd = parts.find((p) => p.type === 'day').value;
        return `${yyyy}-${mm}-${dd}`;
    });

    const DEFAULT_SORT = {
        partName: { direction: null },
        quotes: { direction: 'desc' },
        quantityRequested: { direction: null },
        customerContractPrice: { direction: null },
        listPrice: { direction: null },
    };
    const [sortState, setSortState] = useState(DEFAULT_SORT);

    function mmddyyyyToIso(dateStr) {
        if (!dateStr) return '';
        const [mm, dd, yyyy] = dateStr.split('/');
        if (!mm || !dd || !yyyy) return '';
        return `${yyyy.padStart(4, '0')}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }

    function dynamicConditionCodes(rows = []) {
        // Only show a dynamic set of Condition Codes for the current date that is filtered
        const onlyToday = rows.filter((row) => {
            const isoDate = mmddyyyyToIso(row.CreatedDate);
            return isoDate === filterDate;
        });

        const seen = new Set();
        let hasNull = false;
        for (const r of onlyToday) {
            const code = (r.conditionCodeToDisplay ?? '').toString().trim().toUpperCase();
            if (!code) {
                hasNull = true;
            } else {
                seen.add(code);
            }
        }

        const sorted = Array.from(seen).sort((a, b) => a.localeCompare(b));
        return hasNull ? ['NULL', ...sorted] : sorted;
    }

    async function fetchData(routineRefetch = false) {
        try {
            // Only set the loading state if not a routine refresh
            if (!routineRefetch) setLoading(true);
            const response = await WMSFetch('wms/sms/get-auto-quote-data', { date: filterDate }, currentUser);
            const userResponse = await WMSFetch('wms/sms/get-user-list', {}, currentUser);

            const uniqueSources = {};
            if (response?.length) {
                response.forEach((item) => {
                    uniqueSources[item.inscor__Quote_Source__c] = ' ';
                });
                setSources(Object.keys(uniqueSources));
            }

            setUsers(userResponse);
            setAutoQuoteData(response);
            setConditionCodes(dynamicConditionCodes(response || []));
        } catch (error) {
            logError(error, 'fetchData');
        } finally {
            setLoading(false);
        }
    }

    const handleSetActivePage = (page) => {
        setActivePage(page);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (filterDate) {
            fetchData();
        }
    }, [filterDate]);

    // Scheduled data refresh
    useEffect(() => {
        const interval = setInterval(() => {
            fetchData(true);
        }, 300000);

        return () => clearInterval(interval);
    }, [filterDate]);

    useEffect(() => {
        setShowInStock(false);
        setPartsWithInventory([]);
    }, [filterDate]);

    const renderPage = () => {
        switch (activePage) {
            case PAGE_TYPES.QUOTE_GRID:
                return (
                    <WMS_QuoteGrid
                        setActivePage={handleSetActivePage}
                        data={autoQuoteData}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        loading={loading}
                        currentUser={currentUser}
                        filterDate={filterDate}
                        setFilterDate={setFilterDate}
                        filters={filters}
                        setFilters={setFilters}
                        userList={users}
                        arrowDirections={sortState}
                        setArrowDirections={setSortState}
                        setLoading={setLoading}
                        partsWithInventory={partsWithInventory}
                        setPartsWithInventory={setPartsWithInventory}
                        showInStock={showInStock}
                        setShowInStock={setShowInStock}
                        sources={sources}
                        conditionCodes={conditionCodes}
                        mmddyyyyToIso={mmddyyyyToIso}
                        onRefresh={fetchData}
                        expandAll={expandAll}
                        setExpandAll={setExpandAll}
                    />
                );
            case PAGE_TYPES.QUOTE_SUBMISSION:
                return (
                    <WMS_QuoteSubmission
                        quoteLineIds={selectedRows.map((row) => row.quoteLineId)}
                        partId={selectedRows[0].partId}
                        partName={selectedRows[0].partName}
                        partDescription={selectedRows?.[0]?.partDescription}
                        setActivePage={handleSetActivePage}
                        setAutoQuoteData={setAutoQuoteData}
                    />
                );
            default:
                return <div>Select a page</div>;
        }
    };

    return <div>{renderPage()}</div>;
}
